import React, { useEffect } from "react";
import { FcGoogle } from "react-icons/fc";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  clearError,
  selectAuthError,
  selectAuthInitialized,
  selectAuthLoading,
  selectUser,
  signInWithGoogle,
} from "src/store/authSlice";
import ShootingMeteors from "../components/ShootingMeteors";
import ThemedPage from "../components/ThemedPage";
import type { AppDispatch } from "../store";

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const initialized = useSelector(selectAuthInitialized);

  useEffect(() => {
    // Only redirect if auth is initialized AND user is logged in
    if (initialized && user) {
      navigate("/");
    }
  }, [initialized, user, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      await dispatch(signInWithGoogle()).unwrap();
      navigate("/");
    } catch (err) {
      console.error("Failed to sign in:", err);
    }
  };

  useEffect(() => {
    // Clear error after 5 seconds
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Show loading screen while checking auth state
  if (!initialized) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-indigo-900 via-gray-900 to-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemedPage initialTheme="liftoff">
      <div className="min-h-screen w-full relative overflow-hidden text-white flex items-center justify-center">
        <ShootingMeteors />
        {/* Login Card */}
        <div className="relative z-10 w-full max-w-md px-6">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
            {/* Logo/Title */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent">
                Live Presence
              </h1>
              <p className="text-gray-300 text-sm">
                Sign in to view analytics dashboard
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-200 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <FcGoogle className="text-2xl" />
                  <span>Sign in with Google</span>
                </>
              )}
            </button>

            {/* Info Text */}
            <div className="mt-6 text-center">
              <p className="text-gray-400 text-xs">
                Only authorized users can access the dashboard
              </p>
            </div>
          </div>
        </div>
      </div>
    </ThemedPage>
  );
};

export default Login;
